import {
  FormatAlignCenter,
  FormatAlignLeft,
  FormatAlignRight,
  VerticalAlignBottom,
  VerticalAlignCenter,
  VerticalAlignTop,
} from "@mui/icons-material";
import {
  FormControl,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  ToggleButton,
} from "@mui/material";
import React, { ReactElement } from "react";
import { HexAlphaColorPicker } from "react-colorful";

import {
  TextAlignHorizontal,
  TextAlignVertical,
} from "../../generated/gql/graphql";
import AlignLeftIcon from "../icons/outlines/align-left";
import AlignTopIcon from "../icons/outlines/align-top";
import AlphabetIcon from "../icons/outlines/alphabet";

import { CanvasObjectText } from "./canvas-object-text";
import { useFonts } from "./use-fonts";

const SelectionStyles = {
  "& fieldset": {
    border: 0,
  },
  "& .MuiSelect-icon": {
    display: "none",
  },
  "& .MuiSelect-select": {
    padding: "14px 10px",
    paddingRight: "10px!important",
  },
  "&.MuiInputBase-root": {
    boxShadow: "0 0 15px 0 rgba(0, 0, 0, 0.10)",
    color: "#A1A1A1",
    fontSize: "16px",
    borderRadius: "7px",
  },
};

export type TextToolbarProps = {
  canvasObject: CanvasObjectText;
  objectChanged: (data: Partial<Omit<CanvasObjectText, "kind">>) => void;

  showTextAi: boolean;
  onShowTextAi: (show: boolean) => void;
};

export function TextToolbar({
  canvasObject,
  objectChanged,
  showTextAi,
  onShowTextAi,
}: TextToolbarProps): ReactElement {
  const [showColorPicker, setShowColorPicker] = React.useState(false);
  const [textContent, setTextContent] = React.useState(
    canvasObject.text.content,
  );
  const fontsQuery = useFonts({ query: {} });

  function handleColorChange(color: string): void {
    objectChanged({
      text: {
        ...canvasObject.text,
        color,
      },
    });
  }

  function handleTextContentChange(newContent: string): void {
    objectChanged({
      text: {
        ...canvasObject.text,
        content: newContent,
      },
    });
  }

  function handleAlignHorizontalChange(
    newAlignHorizontal: TextAlignHorizontal,
  ): void {
    objectChanged({
      text: {
        ...canvasObject.text,
        alignHorizontal: newAlignHorizontal,
      },
    });
  }

  function handleAlignVerticalChange(
    newAlignVertical: TextAlignVertical,
  ): void {
    objectChanged({
      text: {
        ...canvasObject.text,
        alignVertical: newAlignVertical,
      },
    });
  }

  function handleFontChange(newFontId: string | null): void {
    objectChanged({
      text: {
        ...canvasObject.text,
        fontId: newFontId,
      },
    });
  }

  function handleOpenAi(): void {
    onShowTextAi(!showTextAi);
  }

  return (
    <>
      <div className="absolute flex justify-between top-[-55px] left-0 w-full px-[10px]">
        <div>
          <FormControl className="ms-3 min-w-[200px]">
            {fontsQuery.data?.fonts.items && (
              <Select
                sx={SelectionStyles}
                value={canvasObject.text.fontId}
                onChange={(e) => handleFontChange(e.target.value)}
                input={
                  <OutlinedInput
                    startAdornment={
                      <InputAdornment position="start">
                        <AlphabetIcon />
                      </InputAdornment>
                    }
                  ></OutlinedInput>
                }
              >
                {fontsQuery.data?.fonts.items?.map((font) => (
                  <MenuItem key={font.id} value={font.id}>
                    {font.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>
          <ToggleButton
            className="ms-3 border-0 p-[10px] hover:bg-white"
            sx={{
              boxShadow: "0 0 15px 0 rgba(0, 0, 0, 0.10)",
            }}
            value="color"
            aria-label="color"
            onChange={() => setShowColorPicker((prevValue) => !prevValue)}
            selected={showColorPicker}
          >
            <div
              style={{
                background: canvasObject.text.color || "rgba(212, 84, 84, 1)",
              }}
              className="w-[30px] h-[30px] rounded-md"
            ></div>
          </ToggleButton>
        </div>
        <div className="mr-[13px]">
          <FormControl className="ms-3 w-[50px]">
            <Select
              // value={canvasObject.text.alignHorizontal}
              sx={{
                ...SelectionStyles,
                "& .MuiSelect-icon": {
                  display: "block",
                  transform: "rotate(0deg)",
                  fontSize: "24px",
                  marginRight: "6px",
                },
                "& .MuiSvgIcon-root": {
                  display: "none",
                },
              }}
              IconComponent={AlignLeftIcon}
              aria-label="align horizontal"
              onChange={(e) =>
                handleAlignHorizontalChange(
                  e.target.value as TextAlignHorizontal,
                )
              }
            >
              <MenuItem value={TextAlignHorizontal.Left}>
                <FormatAlignLeft />
              </MenuItem>
              <MenuItem value={TextAlignHorizontal.Center}>
                <FormatAlignCenter />
              </MenuItem>
              <MenuItem value={TextAlignHorizontal.Right}>
                <FormatAlignRight />
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl className="ms-3 w-[50px]">
            <Select
              sx={{
                ...SelectionStyles,
                "& .MuiSelect-icon": {
                  display: "block",
                  transform: "rotate(0deg)",
                  fontSize: "24px",
                  marginRight: "6px",
                },
                "& .MuiSvgIcon-root": {
                  display: "none",
                },
              }}
              IconComponent={AlignTopIcon}
              // value={canvasObject.text.alignVertical}
              aria-label="align vertical"
              onChange={(e) =>
                handleAlignVerticalChange(e.target.value as TextAlignVertical)
              }
            >
              <MenuItem value={TextAlignVertical.Top}>
                <VerticalAlignTop />
              </MenuItem>
              <MenuItem value={TextAlignVertical.Middle}>
                <VerticalAlignCenter />
              </MenuItem>
              <MenuItem value={TextAlignVertical.Bottom}>
                <VerticalAlignBottom />
              </MenuItem>
            </Select>
          </FormControl>
        </div>
        {/* <TextareaAutosize
          className="ms-3"
          value={textContent}
          onChange={(e) => setTextContent(e.target.value)}
          onBlur={(e) => handleTextContentChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleTextContentChange(textContent);
            }
          }}
        ></TextareaAutosize>
        <ToggleButton
          className="ms-3"
          value={showTextAi}
          onChange={handleOpenAi}
          selected={showTextAi}
        >
          <VerticalAlignBottom />
        </ToggleButton> */}
      </div>
      {showColorPicker && (
        <div
          className="absolute p-[15px] bg-white rounded-md"
          style={{
            zIndex: 2000,
            boxShadow: "0 0 15px 0 rgba(0, 0, 0, 0.10)",
          }}
        >
          <HexAlphaColorPicker
            color={canvasObject.text.color ?? "#000000ff"}
            onChange={handleColorChange}
          />
        </div>
      )}
    </>
  );
}
